import { useCallback, useEffect, useMemo, useState } from "react";
import { useDateRange } from "@/state/DateRangeContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { botQaReportingService } from "@/pages/analytics/services/BotQaReportingService";
import { botQaViewModelDeriver } from "@/pages/analytics/lib/BotQaViewModelDeriver";
import { reportingBotService } from "@/pages/analytics/services/reportingBotService";

/**
 * Workspace-scoped chatbot reporting (reporting-service bot metrics).
 * @param {{ enabled?: boolean }} [opts]
 */
export function useReportingBot({ enabled = true } = {}) {
  const { startDate, endDate } = useDateRange();
  const { activeWorkspaceId } = useWorkspace();
  const effective = Boolean(enabled && activeWorkspaceId);
  const range = useMemo(() => ({ startDate, endDate }), [startDate, endDate]);

  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(/** @type {Error | null} */ (null));
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!effective) {
      setLoading(false);
      setError(null);
      setBot(null);
      return undefined;
    }

    const ac = new AbortController();
    setLoading(true);
    setError(null);

    Promise.all([
      reportingBotService.load(range, ac.signal),
      botQaReportingService.load(range, ac.signal).catch((e) => {
        botQaReportingService.logError(e instanceof Error ? e : new Error(String(e)));
        return null;
      }),
    ])
      .then(([viewModel, qaRaw]) => {
        if (ac.signal.aborted) return;
        const botQa = qaRaw ? botQaViewModelDeriver.build(qaRaw) : null;
        setBot({ ...viewModel, botQa });
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        const err = e instanceof Error ? e : new Error(String(e));
        reportingBotService.logError(err);
        setError(err);
        setBot(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => ac.abort();
  }, [range, tick, effective]);

  return { bot, loading, error, refresh };
}
